import React, { useState, useEffect } from "react";
import { X, Plus, TrendingUp, Calendar, History, DollarSign } from "lucide-react";
import toast from "react-hot-toast";
import { SalaryRevisionItem } from "../../types/staff.types";
import { ClinicStaffService } from "../../services/api/clinic.staff.service";

interface StaffSalaryRevisionModalProps {
  isOpen: boolean;
  staffId: string;
  staffName: string;
  currentSalary?: number;
  onClose: () => void;
  onSuccess: () => void;
}

export const StaffSalaryRevisionModal: React.FC<StaffSalaryRevisionModalProps> = ({
  isOpen,
  staffId,
  staffName,
  currentSalary = 0,
  onClose,
  onSuccess,
}) => {
  const [activeTab, setActiveTab] = useState<"history" | "add">("history");
  const [revisions, setRevisions] = useState<SalaryRevisionItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [previousSalary, setPreviousSalary] = useState<number>(currentSalary);
  const [revisedSalary, setRevisedSalary] = useState<number>(currentSalary);
  const [effectiveDate, setEffectiveDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [remarks, setRemarks] = useState<string>("");

  useEffect(() => {
    if (isOpen && staffId) {
      fetchHistory();
      setPreviousSalary(currentSalary);
      setRevisedSalary(currentSalary);
    }
  }, [isOpen, staffId, currentSalary]);

  const fetchHistory = async () => {
    try {
      setIsLoading(true);
      const res = await ClinicStaffService.getSalaryRevisions(staffId);
      if (res.success && res.data) {
        setRevisions(res.data);
      }
    } catch (error) {
      console.error("Fetch salary revisions error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (revisedSalary <= 0) {
      toast.error("Revised salary must be greater than 0");
      return;
    }

    try {
      setIsSubmitting(true);
      const res = await ClinicStaffService.addSalaryRevision({
        staffId,
        previousSalary: Number(previousSalary),
        revisedSalary: Number(revisedSalary),
        effectiveDate,
        remarks: remarks.trim() || undefined,
      });

      if (res.success) {
        toast.success(res.message || "Salary revision added successfully");
        onSuccess();
        fetchHistory();
        setActiveTab("history");
        setRemarks("");
      } else {
        toast.error(res.message || "Failed to add salary revision");
      }
    } catch (error) {
      console.error("Add revision error:", error);
      toast.error("Error adding salary revision");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-100 w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-150">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Salary Revision Tracker
            </h3>
            <p className="text-xs text-gray-500">
              Staff: <span className="font-medium text-gray-700">{staffName}</span> (Current: ₹{currentSalary.toLocaleString()}/mo)
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab switch */}
        <div className="flex border-b border-gray-100 px-6 pt-2">
          <button
            type="button"
            onClick={() => setActiveTab("history")}
            className={`pb-2.5 px-3 text-xs font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${
              activeTab === "history"
                ? "border-emerald-600 text-emerald-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <History className="w-3.5 h-3.5" />
            Revision History ({revisions.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("add")}
            className={`pb-2.5 px-3 text-xs font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${
              activeTab === "add"
                ? "border-emerald-600 text-emerald-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <Plus className="w-3.5 h-3.5" />
            Add New Revision
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === "history" ? (
            <div className="max-h-72 overflow-y-auto pr-1 space-y-3">
              {isLoading ? (
                <div className="py-8 text-center text-xs text-gray-400">Loading history...</div>
              ) : revisions.length === 0 ? (
                <div className="py-8 text-center">
                  <TrendingUp className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-xs font-medium text-gray-500">No salary revisions recorded yet</p>
                  <button
                    onClick={() => setActiveTab("add")}
                    className="mt-3 text-xs text-emerald-600 font-semibold hover:underline"
                  >
                    + Record First Revision
                  </button>
                </div>
              ) : (
                revisions.map((rev) => {
                  const diff = rev.revisedSalary - rev.previousSalary;
                  const isIncrease = diff >= 0;
                  return (
                    <div
                      key={rev.id}
                      className="p-3.5 rounded-lg border border-gray-100 bg-gray-50/50 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-900">
                            ₹{rev.revisedSalary.toLocaleString()}
                          </span>
                          <span className="text-xs text-gray-400 line-through">
                            ₹{rev.previousSalary.toLocaleString()}
                          </span>
                        </div>
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            isIncrease
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-rose-50 text-rose-700"
                          }`}
                        >
                          {isIncrease ? "+" : ""}₹{diff.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-1.5">
                        <Calendar className="w-3 h-3 text-gray-400" />
                        Effective: {new Date(rev.effectiveDate).toLocaleDateString()}
                      </div>
                      {rev.remarks && (
                        <p className="text-xs text-gray-600 mt-1.5 italic bg-white p-2 rounded border border-gray-100">
                          "{rev.remarks}"
                        </p>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3.5">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Previous Monthly Salary (₹)
                  </label>
                  <input
                    type="number"
                    value={previousSalary}
                    onChange={(e) => setPreviousSalary(Number(e.target.value))}
                    className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Revised Monthly Salary (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={revisedSalary}
                    onChange={(e) => setRevisedSalary(Number(e.target.value))}
                    className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800 font-semibold"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Effective Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Remarks / Reason for Revision
                </label>
                <textarea
                  rows={2}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="e.g. Annual Appraisal, Promotion, Market Correction..."
                  className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800 resize-none"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setActiveTab("history")}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Back to History
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors shadow-sm"
                >
                  {isSubmitting ? "Saving..." : "Save Revision"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
